/**
 * StyledSelect — drop-in replacement for a native <select> that uses
 * Radix/Shadcn Select under the hood. Renders a fully-controlled portal
 * popover so OS chrome cannot break the dark-theme styling (this fixes
 * the recurring "white-text-on-white dropdown" bug on Chrome/Windows
 * where native <select> popups ignore CSS for <option> elements).
 *
 * Usage (near-identical to a native select):
 *
 *   <StyledSelect
 *     value={value}
 *     onChange={(v) => setValue(v)}
 *     placeholder="Select…"
 *     options={[{ value: 'a', label: 'Apartment' }, ...]}
 *     className="w-full"
 *     data-testid="property-category-select"
 *   />
 *
 * `options` may be:
 *   - an array of strings  →  used as both value AND label
 *   - an array of { value, label } objects
 *
 * The trigger inherits the same dark "bg-white/5 border-white/10" look as
 * the rest of the wizard so it's visually indistinguishable from the
 * previous native <select>.
 */
import * as React from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';

const normaliseOptions = (options = []) =>
  options.map((opt) =>
    typeof opt === 'string'
      ? { value: opt, label: opt }
      : { value: String(opt.value ?? ''), label: opt.label ?? String(opt.value ?? '') },
  );

export const StyledSelect = React.forwardRef(
  (
    {
      value,
      onChange,
      onValueChange,
      options = [],
      placeholder = 'Select…',
      className,
      contentClassName,
      disabled,
      ...rest
    },
    ref,
  ) => {
    const normalised = normaliseOptions(options);
    const handleChange = (v) => {
      if (onChange) onChange(v);
      if (onValueChange) onValueChange(v);
    };

    // Radix Select uses "" as a reserved sentinel — coerce empties to undefined
    const safeValue = value === '' || value == null ? undefined : String(value);

    return (
      <Select value={safeValue} onValueChange={handleChange} disabled={disabled}>
        <SelectTrigger
          ref={ref}
          className={cn(
            'bg-white/5 border-white/10 text-white hover:bg-white/[0.07] focus:ring-gold/40 data-[placeholder]:text-text-secondary h-10',
            className,
          )}
          {...rest}
        >
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent
          className={cn(
            'bg-[#141414] border-white/10 text-white shadow-xl',
            contentClassName,
          )}
        >
          {normalised.map((opt) => (
            <SelectItem
              key={opt.value}
              value={opt.value}
              className="text-white focus:bg-white/10 focus:text-white data-[state=checked]:bg-gold/15 data-[state=checked]:text-gold cursor-pointer"
            >
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  },
);

StyledSelect.displayName = 'StyledSelect';

export default StyledSelect;
